import { getGraphGroupMembership } from "./ApiService";
import {
  configuredCorpAccessGroups,
  GroupClaims,
  isMemberOfAny,
  verdictFromClaims,
} from "../utility/corpAccessGroups";
import { logger } from "../utility/logger";

/**
 * Resolves the per-environment CORP group gate: may this corp user use THIS environment?
 *
 * The decision itself is pure and lives in `utility/corpAccessGroups.ts`; this file is the
 * side-effecting half (the Graph lookup), kept separate for the same reason as
 * `supportTenantRuntime.ts` — it gives PrivateRoute one mockable seam and keeps the rules
 * unit-testable without a network.
 */
export type CorpAccessDecision = "allowed" | "denied";

/**
 * Decide from the ID token if it can answer, otherwise ask Microsoft Graph.
 *
 * The claim is the fast path — zero network cost, and cryptographically bound to the token —
 * but it cannot answer in two ordinary situations (a user in 200+ groups whose claim Entra
 * suppressed, and the window before the `groups` optional claim is configured on the app
 * registration). `verdictFromClaims` reports those as `indeterminate` rather than guessing,
 * and this function then settles them with `checkMemberGroups`, which needs no permission
 * beyond the `User.Read` the app already uses.
 *
 * THROWS rather than returning a third "unknown" value when Graph cannot be reached. That is
 * deliberate: PrivateRoute's existing catch already sorts a rejected token (→ fresh sign-in)
 * from an unreachable dependency (→ "We couldn't verify your access"), and neither of those
 * should be reported to the user as "you are not a member". Never treat a failure here as a
 * denial, and never as a pass.
 */
export async function resolveCorpAccess(
  accessToken: string,
  claims: GroupClaims | null | undefined
): Promise<CorpAccessDecision> {
  const required = configuredCorpAccessGroups();

  // No group configured for this environment ⇒ the gate is off and the Swagelok company
  // allow-list alone licenses corp users, exactly as before this existed.
  if (required.length === 0) return "allowed";

  const verdict = verdictFromClaims(claims, required);

  if (verdict === "member") return "allowed";
  if (verdict === "not-member") return "denied";

  logger.log(
    "CORP access group not decidable from the ID token (overage or claim not configured); asking Graph."
  );

  const matched = await getGraphGroupMembership(accessToken, required);

  return isMemberOfAny(matched, required) ? "allowed" : "denied";
}

/**
 * The group ids this environment requires, for the denial screen. Users cannot act on a
 * GUID, but it is what a support engineer needs to see to say which group to add them to —
 * and it is the one piece of the decision that differs per environment.
 */
export function requiredCorpAccessGroups(): string[] {
  return configuredCorpAccessGroups();
}
